import React, { useState } from 'react';
import { Edit, Play, FileText, Target, Settings, TestTube, Webhook, Calendar, X, RefreshCw, CheckCircle, XCircle } from 'lucide-react';

import Button from '../../../components/ui/button';
import Input from '../../../components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../../components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import Badge from '../../../components/ui/badge';

const EvaluationReview = ({
  wizardData,
  onComplete,
  onPrevious,
  loading,
  goToStep
}) => {
  // Get template data
  const templateData = wizardData.template;
  const testData = wizardData.test_data;
  const criteria = wizardData.criteria?.criteria || [];

  console.log('EvaluationReview: wizardData:', wizardData);
  console.log('EvaluationReview: testData:', testData);
  console.log('EvaluationReview: criteria:', criteria);

  // State for optional webhook and scheduling
  const [showWebhookSetup, setShowWebhookSetup] = useState(false);
  const [showSchedulingSetup, setShowSchedulingSetup] = useState(false);
  const [webhookConfig, setWebhookConfig] = useState({
    url: '',
    enabled: false
  });
  const [scheduleConfig, setScheduleConfig] = useState({
    enabled: false,
    frequency: 'daily',
    time: '09:00',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    day_of_week: 1,
    day_of_month: 1
  });
  const [testingWebhook, setTestingWebhook] = useState(false);
  const [webhookTestResult, setWebhookTestResult] = useState(null);

  // Calculate estimated execution time
  const testCases = testData?.data || testData?.testCases || [];
  const estimatedTime = testCases.length
    ? Math.ceil(testCases.length * 2) // Rough estimate: 2 seconds per test case
    : 0;

  // Calculate total weight
  const totalWeight = criteria.reduce((sum, criterion) => sum + (criterion.weight || 1), 0);

  const handleTestWebhook = async () => {
    if (!webhookConfig.url) return;

    setTestingWebhook(true);
    setWebhookTestResult(null);

    try {
      // Simulate webhook test
      const response = await fetch(webhookConfig.url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ test: true, timestamp: new Date().toISOString() })
      });

      setWebhookTestResult({
        success: response.ok,
        status_code: response.status,
        message: response.ok ? 'Webhook test successful' : 'Webhook test failed'
      });
    } catch (error) {
      setWebhookTestResult({
        success: false,
        status_code: 0,
        message: 'Failed to connect to webhook URL'
      });
    } finally {
      setTestingWebhook(false);
    }
  };

  return (
    <div className="p-8 space-y-8">
      <div>
        <h2 className="text-xl font-semibold mb-2">Review Evaluation</h2>
        <p className="text-gray-600">
          Review your evaluation configuration before execution. You can go back to edit any step.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Column - Configuration Summary */}
        <div className="space-y-6">
          {/* Template/Type Selection */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center space-x-2">
                  <FileText className="h-5 w-5 text-blue-600" />
                  <span>Template Selection</span>
                </CardTitle>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.preventDefault();
                    goToStep(0);
                  }} // Go back to template step (index 0)
                  className="text-blue-600"
                >
                  <Edit className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Type:</span>
                  <span className="text-sm font-medium">
                    {templateData?.type === 'template' ? 'Template-based' : 'Custom'}
                  </span>
                </div>
                {templateData?.type === 'template' && templateData.template && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Template:</span>
                      <span className="text-sm font-medium">{templateData.template.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Category:</span>
                      <span className="text-sm font-medium">{templateData.template.category || 'N/A'}</span>
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Test Data Configuration */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center space-x-2">
                  <TestTube className="h-5 w-5 text-green-600" />
                  <span>Test Data</span>
                </CardTitle>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.preventDefault();
                    console.log('EvaluationReview: Clicking Edit Test Data');
                    goToStep(1);
                  }} // Go back to test data step (index 1)
                  className="text-blue-600"
                >
                  <Edit className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Run Name:</span>
                  <span className="text-sm font-medium">{testData?.run_name || testData?.runName || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Example Type:</span>
                  <span className="text-sm font-medium capitalize">
                    {testData?.exampleType?.replace('_', ' ') || 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Model:</span>
                  <span className="text-sm font-medium">
                    {testData?.provider}/{testData?.model}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Test Cases:</span>
                  <span className="text-sm font-medium">
                    {testCases.length} cases
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Role:</span>
                  <span className="text-sm font-medium capitalize">{testData?.role || 'user'}</span>
                </div>
              </div>

              {testData?.prompt && (
                <div className="mt-4 pt-4 border-t">
                  <p className="text-sm text-gray-600 mb-2">Prompt:</p>
                  <div className="bg-gray-50 p-3 rounded text-sm">
                    {testData.prompt.length > 150
                      ? `${testData.prompt.substring(0, 150)}...`
                      : testData.prompt
                    }
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Model Configuration */}
          {testData?.configuration && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center space-x-2">
                  <Settings className="h-5 w-5 text-purple-600" />
                  <span>Model Configuration</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Temperature:</span>
                    <span className="text-sm font-medium">{testData.configuration.temperature}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Max Tokens:</span>
                    <span className="text-sm font-medium">{testData.configuration.maxTokens}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Top P:</span>
                    <span className="text-sm font-medium">{testData.configuration.topP}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Column - Criteria and Execution Info */}
        <div className="space-y-6">
          {/* Evaluation Criteria */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center space-x-2">
                  <Target className="h-5 w-5 text-orange-600" />
                  <span>Evaluation Criteria</span>
                </CardTitle>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.preventDefault();
                    goToStep(2);
                  }} // Go back to criteria step (index 2)
                  className="text-blue-600"
                >
                  <Edit className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {criteria.length > 0 ? (
                <div className="space-y-3">
                  {criteria.map((criterion, index) => (
                    <div key={criterion.id || index} className="border-l-4 border-blue-200 pl-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-sm font-medium">{criterion.name}</p>
                          <p className="text-xs text-gray-500 capitalize">
                            {criterion.type.replace('_', ' ')}
                          </p>
                        </div>
                        <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                          Weight: {criterion.weight}
                        </span>
                      </div>

                      {/* Show brief config summary */}
                      <div className="mt-2 text-xs text-gray-600">
                        {criterion.type === 'model_labels' && criterion.config.labels && (
                          <span>Labels: {criterion.config.labels.join(', ')}</span>
                        )}
                        {criterion.type === 'model_scorer' && (
                          <span>Range: {criterion.config.minScore} - {criterion.config.maxScore}</span>
                        )}
                        {criterion.type === 'string_check' && criterion.config.expectedStrings && (
                          <span>Strings: {criterion.config.expectedStrings.length} expected</span>
                        )}
                        {criterion.type === 'text_similarity' && (
                          <span>Threshold: {criterion.config.threshold} ({criterion.config.method})</span>
                        )}
                        {criterion.type === 'python_grader' && (
                          <span>Custom Python evaluation</span>
                        )}
                      </div>
                    </div>
                  ))}

                  <div className="pt-3 border-t">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Total Criteria:</span>
                      <span className="font-medium">{criteria.length}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Total Weight:</span>
                      <span className="font-medium">{totalWeight}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-500">No criteria defined</p>
              )}
            </CardContent>
          </Card>

          {/* Execution Summary */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center space-x-2">
                <Play className="h-5 w-5 text-green-600" />
                <span>Execution Summary</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Test Cases:</span>
                  <span className="text-sm font-medium">{testCases.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Criteria:</span>
                  <span className="text-sm font-medium">{criteria.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Estimated Time:</span>
                  <span className="text-sm font-medium">
                    {estimatedTime > 0 ? `~${estimatedTime}s` : 'N/A'}
                  </span>
                </div>

                <div className="pt-3 border-t">
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <p className="text-sm text-blue-800 font-medium mb-1">Ready to Execute</p>
                    <p className="text-xs text-blue-600">
                      Your evaluation will run immediately and show real-time results.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Optional Setup Options */}
          <div className="space-y-3">
            <p className="text-sm text-gray-600 font-medium">Optional Setup (Configure Now or Later)</p>

            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={() => setShowWebhookSetup(!showWebhookSetup)}
            >
              <Webhook className="h-4 w-4 mr-2" />
              <span>Set Up Webhook Notifications</span>
              {webhookConfig.enabled && <Badge className="ml-auto" variant="success">Configured</Badge>}
            </Button>

            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={() => setShowSchedulingSetup(!showSchedulingSetup)}
            >
              <Calendar className="h-4 w-4 mr-2" />
              <span>Schedule Recurring Evaluation</span>
              {scheduleConfig.enabled && <Badge className="ml-auto" variant="success">Configured</Badge>}
            </Button>
          </div>

          {/* Webhook Setup Panel */}
          {showWebhookSetup && (
            <Card className="border-blue-200 bg-blue-50">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center space-x-2">
                    <Webhook className="h-5 w-5 text-blue-600" />
                    <span>Webhook Configuration</span>
                  </CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowWebhookSetup(false)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Webhook URL
                  </label>
                  <Input
                    type="url"
                    placeholder="https://your-webhook-endpoint.com/webhook"
                    value={webhookConfig.url}
                    onChange={(e) => setWebhookConfig({ ...webhookConfig, url: e.target.value })}
                  />
                  <p className="text-xs text-gray-600 mt-1">
                    We'll send a POST request when evaluation completes
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="webhook-enabled"
                    checked={webhookConfig.enabled}
                    onChange={(e) => setWebhookConfig({ ...webhookConfig, enabled: e.target.checked })}
                    className="rounded border-gray-300"
                  />
                  <label htmlFor="webhook-enabled" className="text-sm text-gray-700">
                    Enable webhook notifications
                  </label>
                </div>

                {webhookConfig.url && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleTestWebhook}
                    disabled={testingWebhook}
                    className="w-full"
                  >
                    {testingWebhook ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Play className="h-4 w-4 mr-2" />
                    )}
                    Test Webhook
                  </Button>
                )}

                {webhookTestResult && (
                  <div className={`p-3 rounded-md text-sm ${webhookTestResult.success ? 'bg-green-100 text-green-800 border border-green-300' : 'bg-red-100 text-red-800 border border-red-300'}`}>
                    <div className="flex items-center gap-2">
                      {webhookTestResult.success ? (
                        <CheckCircle className="h-4 w-4" />
                      ) : (
                        <XCircle className="h-4 w-4" />
                      )}
                      <span>{webhookTestResult.message}</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Scheduling Setup Panel */}
          {showSchedulingSetup && (
            <Card className="border-purple-200 bg-purple-50">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center space-x-2">
                    <Calendar className="h-5 w-5 text-purple-600" />
                    <span>Schedule Configuration</span>
                  </CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowSchedulingSetup(false)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="schedule-enabled"
                    checked={scheduleConfig.enabled}
                    onChange={(e) => setScheduleConfig({ ...scheduleConfig, enabled: e.target.checked })}
                    className="rounded border-gray-300"
                  />
                  <label htmlFor="schedule-enabled" className="text-sm font-medium text-gray-700">
                    Enable recurring schedule
                  </label>
                </div>

                {scheduleConfig.enabled && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Frequency
                      </label>
                      <Select value={scheduleConfig.frequency} onValueChange={(value) => setScheduleConfig({ ...scheduleConfig, frequency: value })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="daily">Daily</SelectItem>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {scheduleConfig.frequency === 'weekly' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Day of Week
                        </label>
                        <Select value={scheduleConfig.day_of_week.toString()} onValueChange={(value) => setScheduleConfig({ ...scheduleConfig, day_of_week: parseInt(value) })}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="0">Sunday</SelectItem>
                            <SelectItem value="1">Monday</SelectItem>
                            <SelectItem value="2">Tuesday</SelectItem>
                            <SelectItem value="3">Wednesday</SelectItem>
                            <SelectItem value="4">Thursday</SelectItem>
                            <SelectItem value="5">Friday</SelectItem>
                            <SelectItem value="6">Saturday</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {scheduleConfig.frequency === 'monthly' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Day of Month
                        </label>
                        <Input
                          type="number"
                          min="1"
                          max="31"
                          value={scheduleConfig.day_of_month}
                          onChange={(e) => setScheduleConfig({ ...scheduleConfig, day_of_month: parseInt(e.target.value) || 1 })}
                        />
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Time
                      </label>
                      <Input
                        type="time"
                        value={scheduleConfig.time}
                        onChange={(e) => setScheduleConfig({ ...scheduleConfig, time: e.target.value })}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Timezone
                      </label>
                      <Input
                        type="text"
                        value={scheduleConfig.timezone}
                        onChange={(e) => setScheduleConfig({ ...scheduleConfig, timezone: e.target.value })}
                        placeholder="UTC"
                      />
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* Sample Test Cases Preview */}
          {testData?.testCases && testData.testCases.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Sample Test Cases</CardTitle>
                <CardDescription>
                  Preview of your test cases (showing first 2)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {testCases.slice(0, 2).map((testCase, index) => (
                    <div key={index} className="bg-gray-50 p-3 rounded text-sm">
                      <div className="mb-2">
                        <span className="text-gray-600 font-medium">Input:</span>
                        <p className="mt-1">
                          {testCase.input.length > 100
                            ? `${testCase.input.substring(0, 100)}...`
                            : testCase.input
                          }
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-600 font-medium">Expected:</span>
                        <p className="mt-1">
                          {testCase.expected.length > 100
                            ? `${testCase.expected.substring(0, 100)}...`
                            : testCase.expected
                          }
                        </p>
                      </div>
                    </div>
                  ))}

                  {testCases.length > 2 && (
                    <p className="text-xs text-gray-500 text-center">
                      ... and {testCases.length - 2} more test cases
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Validation Warnings */}
      <div className="space-y-3">
        {((!testData?.run_name && !testData?.runName) || !testData?.prompt) && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-yellow-800 text-sm font-medium">⚠️ Missing Required Information</p>
            <ul className="text-yellow-700 text-sm mt-2 space-y-1">
              {!testData?.run_name && !testData?.runName && <li>• Run name is required</li>}
              {!testData?.prompt && <li>• Prompt is required</li>}
            </ul>
          </div>
        )}

        {testCases.length === 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800 text-sm font-medium">❌ No Test Cases</p>
            <p className="text-red-700 text-sm mt-1">
              At least one test case is required to run the evaluation.
            </p>
          </div>
        )}

        {criteria.length === 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800 text-sm font-medium">❌ No Evaluation Criteria</p>
            <p className="text-red-700 text-sm mt-1">
              At least one evaluation criterion is required to score the results.
            </p>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-6 border-t">
        <Button
          variant="outline"
          onClick={onPrevious}
        >
          Previous
        </Button>

        <div className="flex gap-3">


          <Button
            onClick={() => {
              // Store webhook and schedule config in sessionStorage for post-execution setup
              sessionStorage.setItem('pendingWebhookConfig', JSON.stringify(webhookConfig));
              sessionStorage.setItem('pendingScheduleConfig', JSON.stringify(scheduleConfig));
              onComplete();
            }}
            disabled={
              loading ||
              (!testData?.run_name && !testData?.runName) ||
              !testData?.prompt ||
              testCases.length === 0 ||
              criteria.length === 0
            }
            className="px-8"
          >
            {loading ? 'Creating...' : 'Create & Execute Evaluation'}
          </Button>
        </div>
      </div>
    </div >
  );
};

export default EvaluationReview;
